import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Send, ChevronDown, ChevronRight, ArrowLeft, Trash2, Plus, Loader2, Zap, Menu, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api, { streamChat } from "@/lib/api";
import { AGENT_VISUALS } from "@/lib/agents";

export default function Chat() {
  const { agentCode, conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [agents, setAgents] = useState([]);
  const [convs, setConvs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState(location.state?.prefill || "");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [streamToolCalls, setStreamToolCalls] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const activeAgent = agents.find((a) => a.code === agentCode);
  const v = activeAgent ? AGENT_VISUALS[activeAgent.code] : null;

  // Load agents + conversations
  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data.active));
    refreshConvs();
  }, []);

  const refreshConvs = () => {
    api.get("/conversations").then((r) => setConvs(r.data)).catch(() => {});
  };

  // Load messages for current conversation
  useEffect(() => {
    if (conversationId) {
      api.get(`/conversations/${conversationId}/messages`)
        .then((r) => setMessages(r.data.messages || []))
        .catch(() => setMessages([]));
    } else {
      setMessages([]);
    }
    setStreamText("");
    setStreamToolCalls([]);
  }, [conversationId]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText]);

  // Focus input when agent changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [agentCode]);

  // Close mobile drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [agentCode, conversationId]);

  const send = async () => {
    if (!draft.trim() || streaming || !agentCode) return;
    const userMsg = draft.trim();
    setDraft("");
    setStreaming(true);
    setStreamText("");
    setStreamToolCalls([]);

    // Optimistically add user msg
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: userMsg, created_at: new Date().toISOString() }]);

    let acquiredConvId = conversationId;
    let acquiredToolCalls = [];

    await streamChat({
      agent_code: agentCode,
      conversation_id: conversationId,
      message: userMsg,
      onMeta: (data) => {
        acquiredConvId = data.conversation_id;
        acquiredToolCalls = data.tool_calls || [];
        setStreamToolCalls(acquiredToolCalls);
        // If new conversation, update URL
        if (!conversationId && data.conversation_id) {
          navigate(`/app/chat/${agentCode}/${data.conversation_id}`, { replace: true });
        }
      },
      onDelta: (data) => {
        setStreamText((t) => t + data.content);
      },
      onDocument: (doc) => {
        toast.success(`Document généré : ${doc.title}`);
      },
      onDone: () => {
        setStreaming(false);
        // refresh messages from server to get persisted assistant message
        if (acquiredConvId) {
          api.get(`/conversations/${acquiredConvId}/messages`)
            .then((r) => {
              setMessages(r.data.messages || []);
              setStreamText("");
              setStreamToolCalls([]);
            });
        }
        refreshConvs();
      },
      onError: (err) => {
        setStreaming(false);
        toast.error(err.message || "Erreur de streaming");
      },
    });
  };

  const deleteConv = async (id, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/conversations/${id}`);
    refreshConvs();
    if (id === conversationId) navigate(`/app/chat/${agentCode}`);
    toast.success("Conversation supprimée");
  };

  return (
    <ConsoleShell>
      <div className="max-w-[1600px] mx-auto h-[calc(100vh-4rem-3.5rem)] md:h-[calc(100vh-4rem)] grid grid-cols-1 md:grid-cols-[260px_1fr] lg:grid-cols-[280px_1fr_320px] relative">
        {/* Mobile drawer overlay */}
        {drawerOpen && (
          <button
            className="md:hidden fixed inset-0 bg-black/70 backdrop-blur-sm z-30"
            onClick={() => setDrawerOpen(false)}
            aria-label="Fermer le menu"
            data-testid="chat-drawer-overlay"
          />
        )}

        {/* Sidebar — agents + conversations. Drawer on mobile, static column on md+. */}
        <aside
          className={`border-r border-white/10 flex flex-col overflow-hidden bg-black z-40 transition-transform duration-200
            md:relative md:translate-x-0 md:bg-transparent md:z-auto
            fixed top-0 left-0 h-full w-[85%] max-w-[320px]
            ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="chat-sidebar"
        >
          {/* Mobile close button */}
          <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-sm font-display font-semibold">Agents & conversations</div>
            <button
              onClick={() => setDrawerOpen(false)}
              data-testid="chat-drawer-close"
              className="p-1.5 hover:bg-white/10 transition"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Agents</div>
            <div className="space-y-1">
              {agents.map((a) => {
                const av = AGENT_VISUALS[a.code];
                const active = a.code === agentCode;
                return (
                  <button
                    key={a.code}
                    onClick={() => navigate(`/app/chat/${a.code}`)}
                    data-testid={`sidebar-agent-${a.code}`}
                    className={`w-full flex items-center gap-3 p-2.5 transition text-left ${active ? "bg-white/10" : "hover:bg-white/5"}`}
                  >
                    <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0" style={{ boxShadow: `0 0 0 1.5px ${a.color}` }}>
                      <img src={av?.avatar} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.name}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{a.role}</div>
                    </div>
                    {active && <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="p-4 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Conversations</div>
              {agentCode && (
                <button
                  onClick={() => navigate(`/app/chat/${agentCode}`)}
                  data-testid="new-conv-btn"
                  className="p-1 hover:bg-white/10 transition"
                  title="Nouvelle conversation"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {convs.length === 0 ? (
              <div className="text-xs text-zinc-600 leading-relaxed">Vos conversations apparaîtront ici.</div>
            ) : (
              <ul className="space-y-0.5">
                {convs.map((c) => {
                  const av = AGENT_VISUALS[c.agent_code];
                  const isActive = c.id === conversationId;
                  return (
                    <li key={c.id}>
                      <Link
                        to={`/app/chat/${c.agent_code}/${c.id}`}
                        data-testid={`sidebar-conv-${c.id}`}
                        className={`group flex items-center gap-2 p-2 text-sm transition ${isActive ? "bg-white/10" : "hover:bg-white/5"}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: av?.color || "#fff" }} />
                        <span className="truncate flex-1">{c.title}</span>
                        <button
                          onClick={(e) => deleteConv(c.id, e)}
                          className="text-zinc-500 hover:text-red-400 p-0.5"
                          title="Supprimer cette conversation"
                          data-testid={`delete-conv-${c.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Main chat */}
        <section className="flex flex-col overflow-hidden">
          {/* Agent header */}
          {activeAgent ? (
            <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-white/10 flex items-center gap-3 lg:gap-4 glass" data-testid="chat-header">
              <button
                onClick={() => setDrawerOpen(true)}
                data-testid="chat-drawer-open"
                className="md:hidden p-1.5 -ml-1 hover:bg-white/10 transition flex-shrink-0"
                aria-label="Ouvrir le menu des agents"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-full overflow-hidden flex-shrink-0" style={{ boxShadow: `0 0 0 2px ${activeAgent.color}` }}>
                <img src={v?.avatar} alt={activeAgent.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg lg:text-xl font-semibold tracking-tight">{activeAgent.name}</h2>
                  <span className="text-xs text-emerald-400 font-mono hidden sm:inline">● online</span>
                </div>
                <div className="text-xs text-zinc-500 truncate">{activeAgent.role}</div>
              </div>
              {conversationId && (
                <button
                  onClick={(e) => deleteConv(conversationId, e)}
                  data-testid="chat-header-delete-btn"
                  className="text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 px-2.5 lg:px-3 py-1.5 transition inline-flex items-center gap-1.5 flex-shrink-0"
                  title="Supprimer cette conversation"
                >
                  <Trash2 className="w-3 h-3" /> <span className="hidden sm:inline">Supprimer</span>
                </button>
              )}
              <Link to={`/app/agents/${activeAgent.code}`} className="text-xs text-zinc-400 hover:text-white hidden lg:inline">profil →</Link>
            </div>
          ) : (
            <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-white/10 text-sm text-zinc-500 flex items-center gap-3">
              <button
                onClick={() => setDrawerOpen(true)}
                data-testid="chat-drawer-open"
                className="md:hidden p-1.5 -ml-1 hover:bg-white/10 transition"
                aria-label="Ouvrir le menu des agents"
              >
                <Menu className="w-5 h-5" />
              </button>
              Sélectionnez un agent
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-8 py-6 space-y-6" data-testid="chat-messages">
            {messages.length === 0 && !streaming && activeAgent && v && (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-4" style={{ boxShadow: `0 0 0 2px ${activeAgent.color}` }}>
                  <img src={v.avatar} alt="" className="w-full h-full object-cover" />
                </div>
                <h3 className="font-display text-2xl font-semibold mb-2">{activeAgent.name}</h3>
                <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">{v.description}</p>
                <div className="grid sm:grid-cols-3 gap-2 max-w-2xl mx-auto">
                  {v.examples.map((ex, i) => (
                    <button
                      key={i}
                      onClick={() => setDraft(ex)}
                      data-testid={`chat-example-${i}`}
                      className="text-left p-3 text-xs border border-white/10 hover:border-white/30 hover:bg-white/5 transition"
                    >
                      &quot;{ex}&quot;
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} msg={m} agent={activeAgent} />
            ))}

            {streaming && (
              <div className="flex gap-3" data-testid="streaming-message">
                <Avatar agent={activeAgent} small />
                <div className="flex-1 max-w-3xl">
                  {streamToolCalls.length > 0 && <ToolCalls calls={streamToolCalls} agent={activeAgent} />}
                  <div className="text-sm chat-prose">
                    {streamText ? (
                      <RenderMarkdown content={streamText} />
                    ) : (
                      <div className="inline-flex items-center gap-1 text-zinc-500">
                        <span className="typing-dot w-1.5 h-1.5 bg-zinc-500 rounded-full inline-block" />
                        <span className="typing-dot w-1.5 h-1.5 bg-zinc-500 rounded-full inline-block" />
                        <span className="typing-dot w-1.5 h-1.5 bg-zinc-500 rounded-full inline-block" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-white/10 p-4 lg:p-6">
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="flex items-end gap-3 max-w-4xl mx-auto"
              data-testid="chat-input-form"
            >
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={activeAgent ? `Parlez à ${activeAgent.name}…` : "Sélectionnez un agent pour démarrer"}
                disabled={!activeAgent || streaming}
                rows={1}
                data-testid="chat-input"
                className="flex-1 resize-none bg-[#141414] border border-white/10 focus:border-white/40 outline-none px-4 py-3 text-sm leading-relaxed min-h-[48px] max-h-32 transition"
              />
              <button
                type="submit"
                disabled={!draft.trim() || streaming || !activeAgent}
                data-testid="chat-send-btn"
                className="h-12 px-5 bg-white text-black hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-2"
              >
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
            <div className="text-[10px] text-zinc-600 mt-2 text-center font-mono">
              Entrée pour envoyer · Maj+Entrée pour nouvelle ligne · les données sont récupérées en direct de CHANTIX
            </div>
          </div>
        </section>

        {/* Right panel — context (desktop only) */}
        <aside className="hidden lg:flex flex-col border-l border-white/10 overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Contexte temps réel</div>
            <h3 className="font-display text-base">Connexion CHANTIX</h3>
          </div>
          <div className="p-5 space-y-4 text-xs font-mono text-zinc-400 overflow-y-auto">
            <ToolCallsPanel calls={streamToolCalls.length ? streamToolCalls : messages.flatMap((m) => m.tool_calls || []).slice(-5)} />
          </div>
        </aside>
      </div>
    </ConsoleShell>
  );
}

function Avatar({ agent, small }) {
  const v = AGENT_VISUALS[agent?.code];
  const sz = small ? "w-8 h-8" : "w-10 h-10";
  return (
    <div className={`${sz} rounded-full overflow-hidden flex-shrink-0`} style={{ boxShadow: `0 0 0 1.5px ${agent?.color || "#fff"}` }}>
      {v?.avatar && <img src={v.avatar} alt="" className="w-full h-full object-cover" />}
    </div>
  );
}

function MessageBubble({ msg, agent }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end group" data-testid="msg-user">
        <div className="relative max-w-2xl">
          <div className="bg-white text-black px-4 py-3 text-sm leading-relaxed select-text whitespace-pre-wrap break-words">
            {msg.content}
          </div>
          <CopyButton text={msg.content} position="left" testid="copy-user-msg" />
        </div>
      </div>
    );
  }
  const { drafts, cleaned } = extractEmailDrafts(msg.content);
  return (
    <div className="flex gap-3 group" data-testid="msg-assistant">
      <Avatar agent={agent} small />
      <div className="flex-1 max-w-3xl relative">
        {msg.tool_calls?.length > 0 && <ToolCalls calls={msg.tool_calls} agent={agent} />}
        <div className="text-sm chat-prose text-zinc-100 select-text">
          <RenderMarkdown content={cleaned} />
        </div>
        {drafts.map((d, i) => (
          <EmailDraftCard key={i} draft={d} />
        ))}
        <div className="mt-2 flex items-center gap-2">
          <CopyButton text={msg.content} position="inline" testid="copy-assistant-msg" />
        </div>
      </div>
    </div>
  );
}

function extractEmailDrafts(content) {
  if (!content || !content.includes("[EMAIL_DRAFT]")) {
    return { drafts: [], cleaned: content };
  }
  const drafts = [];
  const cleaned = content.replace(/\[EMAIL_DRAFT\]([\s\S]*?)\[\/EMAIL_DRAFT\]/g, (_, raw) => {
    try {
      const parsed = JSON.parse(raw.trim());
      drafts.push({
        to: String(parsed.to || ""),
        subject: String(parsed.subject || ""),
        body: String(parsed.body || ""),
      });
    } catch {
      // Malformed JSON — ignore but keep the block visible so user knows
      return "";
    }
    return "";
  });
  return { drafts, cleaned: cleaned.trim() };
}

function EmailDraftCard({ draft }) {
  const [to, setTo] = useState(draft.to);
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [editing, setEditing] = useState(!draft.to);
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [sent, setSent] = useState(false);

  const ensureDraft = async () => {
    if (savedId) return savedId;
    const { data } = await api.post("/emails", { to, subject, body });
    setSavedId(data.id);
    return data.id;
  };

  const send = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Tous les champs sont requis");
      return;
    }
    if (!confirm(`Envoyer cet email à ${to} ?`)) return;
    setBusy(true);
    try {
      const id = await ensureDraft();
      if (editing) {
        await api.patch(`/emails/${id}`, { to, subject, body });
      }
      await api.post(`/emails/${id}/send`);
      setSent(true);
      toast.success(`Email envoyé à ${to} ✨`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Tous les champs sont requis");
      return;
    }
    setBusy(true);
    try {
      if (savedId) {
        await api.patch(`/emails/${savedId}`, { to, subject, body });
      } else {
        await ensureDraft();
      }
      setEditing(false);
      toast.success("Brouillon enregistré dans /app/emails");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="my-3 border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="email-card-sent">
        <div className="flex items-center gap-2 text-emerald-400 text-sm">
          <span>✓</span>
          <span>Email envoyé à <span className="font-mono">{to}</span></span>
        </div>
      </div>
    );
  }

  return (
    <div className="my-3 border border-pink-500/30 bg-pink-500/[0.03]" data-testid="email-draft-card">
      <div className="px-4 py-2 border-b border-pink-500/20 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-pink-400">
        <span>✨ Brouillon CLARA · gabryx@chantix.pro</span>
      </div>
      <div className="p-4 space-y-3">
        {editing ? (
          <>
            <input
              value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="destinataire@example.com" type="email"
              data-testid="email-card-to-input"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
            />
            <input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet"
              data-testid="email-card-subject-input"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
            />
            <textarea
              value={body} onChange={(e) => setBody(e.target.value)}
              rows={8} placeholder="Corps du mail"
              data-testid="email-card-body-input"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-y leading-relaxed"
            />
          </>
        ) : (
          <>
            <div className="grid grid-cols-[60px_1fr] gap-2 text-xs">
              <div className="text-zinc-500 uppercase tracking-wider">À</div>
              <div className="font-mono text-zinc-200">{to || <span className="text-amber-400">(à compléter)</span>}</div>
              <div className="text-zinc-500 uppercase tracking-wider">Objet</div>
              <div className="font-medium text-zinc-100">{subject}</div>
            </div>
            <div className="border-t border-white/5 pt-3 text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed select-text" data-testid="email-card-body">
              {body}
            </div>
          </>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            onClick={send}
            disabled={busy}
            data-testid="email-card-send"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Envoyer
          </button>
          <button
            onClick={() => setEditing(!editing)}
            disabled={busy}
            data-testid="email-card-edit"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-zinc-300 hover:bg-white/5 transition disabled:opacity-50"
          >
            {editing ? "Aperçu" : "Modifier"}
          </button>
          <button
            onClick={saveDraft}
            disabled={busy}
            data-testid="email-card-save"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-zinc-300 hover:bg-white/5 transition disabled:opacity-50"
          >
            Enregistrer brouillon
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyButton({ text, position = "inline", testid }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older mobile browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  if (position === "left") {
    // Absolute button to the left of a user (white) bubble
    return (
      <button
        onClick={copy}
        data-testid={testid}
        title="Copier"
        aria-label="Copier le message"
        className={`absolute right-full top-1 mr-2 p-1.5 border border-white/10 bg-black/80 backdrop-blur-sm text-zinc-300 hover:text-white hover:border-white/30 transition
          opacity-100 md:opacity-0 md:group-hover:opacity-100 ${copied ? "!opacity-100" : ""}`}
      >
        {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      </button>
    );
  }

  return (
    <button
      onClick={copy}
      data-testid={testid}
      title="Copier"
      aria-label="Copier le message"
      className={`inline-flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-white border border-transparent hover:border-white/10 px-2 py-1 transition
        opacity-100 md:opacity-0 md:group-hover:opacity-100 ${copied ? "!opacity-100 text-emerald-400" : ""}`}
    >
      {copied ? (
        <>
          <Check className="w-3 h-3" /> Copié
        </>
      ) : (
        <>
          <Copy className="w-3 h-3" /> Copier
        </>
      )}
    </button>
  );
}

function ToolCalls({ calls, agent }) {
  const [open, setOpen] = useState(false);
  if (!calls?.length) return null;
  return (
    <div className="mb-3 border border-white/10 bg-black/60 font-mono text-[11px]" data-testid="tool-calls">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition text-left"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Zap className="w-3 h-3" style={{ color: agent?.color || "#fff" }} />
        <span className="text-zinc-300">{calls.length} appel{calls.length > 1 ? "s" : ""} CHANTIX</span>
        <span className="text-zinc-500 ml-auto">{calls.every((c) => c.ok) ? "✓ OK" : "⚠ partial"}</span>
      </button>
      {open && (
        <div className="px-3 py-2 border-t border-white/5 space-y-1">
          {calls.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className={c.ok ? "text-emerald-400" : "text-red-400"}>{c.ok ? "✓" : "✗"}</span>
              <span className="text-zinc-300">GET {c.endpoint}</span>
              {c.count !== undefined && <span className="text-zinc-500">→ {c.count} items</span>}
              {c.error && <span className="text-red-400 truncate">{c.error}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ToolCallsPanel({ calls }) {
  if (!calls?.length) {
    return (
      <div className="text-zinc-600 leading-relaxed">
        Les appels API CHANTIX déclenchés par les agents s&apos;affichent ici.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {calls.map((c, i) => (
        <div key={i} className="border border-white/10 p-2.5">
          <div className="flex items-center gap-2">
            <span className={c.ok ? "text-emerald-400" : "text-red-400"}>{c.ok ? "✓" : "✗"}</span>
            <span className="text-zinc-300">GET</span>
          </div>
          <div className="text-zinc-400 mt-1 break-all">{c.endpoint}</div>
          {c.count !== undefined && <div className="text-zinc-500 mt-1">{c.count} résultats</div>}
        </div>
      ))}
    </div>
  );
}

/** Very simple markdown-ish renderer (no external deps to keep things light). */
function RenderMarkdown({ content }) {
  // Replace **bold**, `code`, headings, then split paragraphs/lists.
  const lines = content.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${out.length}`} className="list-disc">
          {listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(l) }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (/^[-*]\s+/.test(trimmed)) {
      listBuf.push(trimmed.replace(/^[-*]\s+/, ""));
    } else if (/^#{1,3}\s+/.test(trimmed)) {
      flushList();
      const lvl = trimmed.match(/^(#{1,3})\s+/)[1].length;
      const text = trimmed.replace(/^#{1,3}\s+/, "");
      const Tag = `h${lvl}`;
      out.push(<Tag key={idx} dangerouslySetInnerHTML={{ __html: inline(text) }} />);
    } else if (trimmed === "") {
      flushList();
    } else {
      flushList();
      out.push(<p key={idx} dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushList();
  return <>{out}</>;
}

function inline(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}
